import React, { useState } from 'react';
import {
  Search,
  Plus,
  MoreVertical,
  Mail,
  FileText,
  DollarSign,
  Send,
  Download,
  RefreshCw,
  X,
  Check,
} from 'lucide-react';

interface BillingRecord {
  id: string;
  studentId: string;
  studentName: string;
  studentAvatar?: string;
  planName: string;
  amount: number;
  discount: number;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue' | 'partial';
  paymentDate?: string;
}

const mockBillingRecords: BillingRecord[] = [
  {
    id: '1',
    studentId: 's1',
    studentName: 'Yossi Levy',
    planName: 'Annual Mathematics Package',
    amount: 2745,
    discount: 0,
    dueDate: '2024-02-15',
    status: 'paid',
    paymentDate: '2024-02-10',
  },
  {
    id: '2',
    studentId: 's2',
    studentName: 'Maya Cohen',
    planName: 'Physics Course Bundle',
    amount: 2745,
    discount: 200,
    dueDate: '2024-02-20',
    status: 'pending',
  },
  {
    id: '3',
    studentId: 's3',
    studentName: 'Daniel Green',
    planName: 'Monthly Subscription',
    amount: 199,
    discount: 0,
    dueDate: '2024-01-15',
    status: 'overdue',
  },
  {
    id: '4',
    studentId: 's4',
    studentName: 'Noa Shapiro',
    planName: 'Single Chemistry Course',
    amount: 450,
    discount: 50,
    dueDate: '2024-02-28',
    status: 'partial',
  },
  {
    id: '5',
    studentId: 's5',
    studentName: 'Avi Brown',
    planName: 'Annual Mathematics Package',
    amount: 2745,
    discount: 100,
    dueDate: '2024-03-01',
    status: 'pending',
  },
];

const statusStyles: Record<BillingRecord['status'], { bg: string; text: string; label: string }> = {
  paid: { bg: 'bg-green-100', text: 'text-green-700', label: 'Paid' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
  partial: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partial' },
};

export function BillingTab() {
  const [records, setRecords] = useState<BillingRecord[]>(mockBillingRecords);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [showSendBillingModal, setShowSendBillingModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BillingRecord | null>(null);

  const filteredRecords = records.filter(record => {
    const matchesSearch = record.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.planName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleGiveDiscount = (record: BillingRecord) => {
    setSelectedRecord(record);
    setShowDiscountModal(true);
    setContextMenuId(null);
  };

  return (
    <div>
      {/* Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="partial">Partial</option>
          </select>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search students or plans..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            New Report
          </button>
          <button
            onClick={() => setShowSendBillingModal(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Send Billing
          </button>
          <button className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Billing
          </button>
        </div>
      </div>

      {/* Billing Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  {/* Student */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                        {record.studentName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{record.studentName}</div>
                      </div>
                    </div>
                  </td>

                  {/* Plan Name */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-900">{record.planName}</span>
                  </td>

                  {/* Amount */}
                  <td className="px-4 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      ${(record.amount - record.discount).toLocaleString()}
                    </span>
                    {record.discount > 0 && (
                      <span className="text-xs text-gray-500 line-through ml-1">
                        ${record.amount.toLocaleString()}
                      </span>
                    )}
                  </td>

                  {/* Due Date */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">
                      {new Date(record.dueDate).toLocaleDateString()}
                    </span>
                  </td>

                  {/* Discount */}
                  <td className="px-4 py-4">
                    {record.discount > 0 ? (
                      <span className="text-sm text-green-600">-${record.discount}</span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[record.status].bg} ${statusStyles[record.status].text}`}>
                      {statusStyles[record.status].label}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Send message"
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setContextMenuId(contextMenuId === record.id ? null : record.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {/* Context Menu */}
                        {contextMenuId === record.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                            <button
                              onClick={() => setContextMenuId(null)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <FileText className="w-4 h-4" />
                              Manage Courses
                            </button>
                            <button
                              onClick={() => setContextMenuId(null)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <FileText className="w-4 h-4" />
                              See Invoice
                            </button>
                            <button
                              onClick={() => setContextMenuId(null)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Send className="w-4 h-4" />
                              Resend Invoice
                            </button>
                            <button
                              onClick={() => handleGiveDiscount(record)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <DollarSign className="w-4 h-4" />
                              Add Discount
                            </button>
                            <button
                              onClick={() => setContextMenuId(null)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Mail className="w-4 h-4" />
                              Send Reminder
                            </button>
                            <button
                              onClick={() => setContextMenuId(null)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Download className="w-4 h-4" />
                              Resend Receipt
                            </button>
                            <hr className="my-1" />
                            <button
                              onClick={() => setContextMenuId(null)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <DollarSign className="w-4 h-4" />
                              See Finance
                            </button>
                            <button
                              onClick={() => setContextMenuId(null)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Refund/Return
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRecords.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No billing records found</p>
          </div>
        )}
      </div>

      {/* Send Billing Modal */}
      {showSendBillingModal && (
        <SendBillingModal onClose={() => setShowSendBillingModal(false)} />
      )}

      {/* Give Discount Modal */}
      {showDiscountModal && selectedRecord && (
        <DiscountModal
          record={selectedRecord}
          onClose={() => {
            setShowDiscountModal(false);
            setSelectedRecord(null);
          }}
          onApply={(discount) => {
            setRecords(records.map(r =>
              r.id === selectedRecord.id ? { ...r, discount } : r
            ));
            setShowDiscountModal(false);
            setSelectedRecord(null);
          }}
        />
      )}
    </div>
  );
}

function SendBillingModal({ onClose }: { onClose: () => void }) {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState<'fixed' | 'percent'>('fixed');
  const [recipients, setRecipients] = useState<string[]>([]);

  const basePrice = 10000;
  const labFees = 10.5;
  const extraFees = 0;
  const total = basePrice + labFees + extraFees;
  const discountAmount = discountType === 'fixed' ? discountValue : (total * discountValue / 100);
  const finalPrice = total - discountAmount;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 bg-teal-600 text-white rounded-t-2xl">
            <h2 className="text-lg font-semibold">Send Billing Form</h2>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <div className="p-6 space-y-5">
            {/* Courses or Plan Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Courses or Plan Type
              </label>
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select a plan...</option>
                <option value="year1">Year 1</option>
                <option value="bundle">Physics Bundle</option>
                <option value="subscription">Monthly Subscription</option>
              </select>
            </div>

            {/* Price Breakdown */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Full Price</span>
                <span className="font-medium">${basePrice.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Lab fees</span>
                <span className="font-medium">${labFees}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Extra Fee's</span>
                <span className="font-medium">${extraFees}</span>
              </div>
            </div>

            {/* Give Discount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Give Discount
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                  placeholder="How much do we want to Discount"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setDiscountType('fixed')}
                    className={`px-3 py-2 text-sm ${discountType === 'fixed' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700'}`}
                  >
                    $
                  </button>
                  <button
                    onClick={() => setDiscountType('percent')}
                    className={`px-3 py-2 text-sm ${discountType === 'percent' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700'}`}
                  >
                    %
                  </button>
                </div>
              </div>
            </div>

            {/* Send to */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Send to
              </label>
              <select
                multiple
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[80px]"
              >
                <option value="specific">Specific Students</option>
                <option value="everyone">Everyone</option>
                <option value="grade10">Grade 10</option>
                <option value="grade11">Grade 11</option>
              </select>
            </div>

            {/* Total Summary */}
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total <span className="text-xs">Monthly</span></span>
                <span className="font-medium">${total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount <span className="text-xs">%</span></span>
                <span className="font-medium text-green-600">${discountAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="font-medium text-teal-600">Final Price</span>
                <span className="text-xl font-bold text-teal-600">${finalPrice.toFixed(2)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Reset
              </button>
              <button className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DiscountModal({
  record,
  onClose,
  onApply,
}: {
  record: BillingRecord;
  onClose: () => void;
  onApply: (discount: number) => void;
}) {
  const [discountValue, setDiscountValue] = useState(record.discount);
  const [discountType, setDiscountType] = useState<'fixed' | 'percent'>('fixed');

  const handleApply = () => {
    const discount = discountType === 'fixed'
      ? discountValue
      : (record.amount * discountValue / 100);
    onApply(discount);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Give Discount</h3>

        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-2">Discount Value</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setDiscountType('fixed')}
                className={`px-3 py-2 text-sm ${discountType === 'fixed' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700'}`}
              >
                $
              </button>
              <button
                onClick={() => setDiscountType('percent')}
                className={`px-3 py-2 text-sm ${discountType === 'percent' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700'}`}
              >
                %
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
